import { useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Clock, Loader2, PartyPopper, Users, MessageSquare, Lock } from "lucide-react";

const timeLeft = (iso) => {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "Ending now";
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
};

const Field = ({ id, label, ...props }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-foreground/75 text-sm">{label}</Label>
    <Input id={id} required className="bg-ink border-white/10 h-11 focus-visible:ring-terracotta/50" {...props} />
  </div>
);

const AuthCard = ({ token }) => {
  const { login, setUser } = useAuth();
  const [mode, setMode] = useState("signup");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setUnverified(false);
    try {
      if (mode === "signup") {
        const { data } = await api.post("/auth/register-watch-party", { ...form, share_token: token });
        setUser(data);
        toast.success(`Welcome to Cinevoya, ${data.name}! Taking you into the party…`);
      } else {
        const data = await login(form.email, form.password);
        toast.success(`Welcome back${data?.name ? `, ${data.name}` : ""}`);
      }
    } catch (err) {
      if (mode === "login" && err.response?.status === 403) setUnverified(true);
      else toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const tab = (m, label) => (
    <button
      type="button"
      onClick={() => setMode(m)}
      className={`flex-1 h-10 rounded-full text-sm font-semibold transition-colors ${mode === m ? "bg-terracotta text-white" : "text-foreground/60 hover:text-foreground"}`}
      data-testid={`wp-gate-tab-${m}`}
    >
      {label}
    </button>
  );

  return (
    <div className="w-full max-w-md rounded-2xl bg-surface/85 backdrop-blur-xl border border-white/10 p-6 sm:p-8 shadow-2xl shadow-black/40" data-testid="wp-gate-auth-card">
      <div className="flex gap-1 p-1 rounded-full bg-ink border border-white/10 mb-6">
        {tab("signup", "Sign up free")}
        {tab("login", "Log in")}
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid={`wp-gate-${mode}-form`}>
        {mode === "signup" && (
          <Field id="wp-name" label="Your name" value={form.name} onChange={set("name")} maxLength={80} autoComplete="name" data-testid="wp-gate-name-input" />
        )}
        <Field id="wp-email" label="Email" type="email" value={form.email} onChange={set("email")} autoComplete="email" data-testid="wp-gate-email-input" />
        <Field
          id="wp-password"
          label="Password"
          type="password"
          value={form.password}
          onChange={set("password")}
          minLength={mode === "signup" ? 8 : undefined}
          autoComplete={mode === "signup" ? "new-password" : "current-password"}
          placeholder={mode === "signup" ? "At least 8 characters" : ""}
          data-testid="wp-gate-password-input"
        />
        {unverified && (
          <p className="text-xs text-gold bg-gold/10 border border-gold/30 rounded-lg p-3" data-testid="wp-gate-unverified">
            Please confirm your email first — open the verification link we sent you, then log in here.
          </p>
        )}
        <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-terracotta hover:bg-ember text-white font-semibold text-base" data-testid="wp-gate-submit-btn">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {mode === "signup" ? "Create free account & join" : "Log in & join"}
        </Button>
      </form>
      <p className="text-xs text-foreground/50 text-center mt-5">
        {mode === "signup" ? (
          <>Already on Cinevoya? <button type="button" onClick={() => setMode("login")} className="text-terracotta hover:text-ember font-semibold" data-testid="wp-gate-switch-login">Log in</button></>
        ) : (
          <>New here? <button type="button" onClick={() => setMode("signup")} className="text-terracotta hover:text-ember font-semibold" data-testid="wp-gate-switch-signup">Create a free account</button></>
        )}
      </p>
      {mode === "signup" && (
        <p className="text-[11px] text-foreground/40 text-center mt-3">Free forever. We&apos;ll email a link to confirm your address.</p>
      )}
    </div>
  );
};

export default function WatchPartyGate({ party, token }) {
  const left = timeLeft(party.expires_at);
  return (
    <div className="relative min-h-screen bg-ink grain overflow-hidden" data-testid="wp-gate">
      {party.poster_available && (
        <img src={`${API}/films/${party.film_id}/poster`} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 blur-sm scale-105" />
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/90 to-ink/60" />
      <div className="absolute -top-40 -left-32 w-[560px] h-[560px] rounded-full bg-terracotta/20 blur-[140px] pointer-events-none" />
      <div className="relative z-10 min-h-screen flex flex-col px-6 sm:px-12 lg:px-20 py-8">
        <Logo size={36} className="self-start" />
        <div className="flex-1 grid lg:grid-cols-[1fr_auto] gap-12 items-center py-12">
          <div className="max-w-xl fade-up">
            <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold font-semibold mb-5" data-testid="wp-gate-eyebrow">
              <PartyPopper className="w-4 h-4" /> You&apos;re invited to a Watch Party
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95]" data-testid="wp-gate-film-title">
              {party.film_title}
            </h1>
            <p className="text-foreground/70 mt-5 text-base sm:text-lg leading-relaxed">
              <span className="text-foreground font-semibold" data-testid="wp-gate-host">{party.host_name || "A friend"}</span> is hosting a private screening on Cinevoya, the home of African short films. Watch together and chat live.
            </p>
            {party.film_synopsis && <p className="text-sm text-foreground/50 mt-4 line-clamp-3">{party.film_synopsis}</p>}
            <div className="flex flex-wrap gap-x-6 gap-y-3 mt-8 text-sm text-foreground/70">
              {left && <span className="flex items-center gap-2" data-testid="wp-gate-time-left"><Clock className="w-4 h-4 text-terracotta" />{left}</span>}
              {party.viewer_limit ? <span className="flex items-center gap-2" data-testid="wp-gate-seats"><Users className="w-4 h-4 text-terracotta" />{Math.max(0, party.viewer_limit - (party.seats_taken || 1))} of {party.viewer_limit} seats left</span> : null}
              <span className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-terracotta" />Live party chat</span>
            </div>
            <p className="flex items-center gap-2 text-xs text-foreground/45 mt-8">
              <Lock className="w-3.5 h-3.5" /> A free Cinevoya account is needed to join. It takes 20 seconds, and the host has already paid for your seat.
            </p>
          </div>
          {party.viewer_limit && (party.seats_taken || 1) >= party.viewer_limit ? (
            <div className="rounded-2xl border border-gold/30 bg-gold/5 p-6 max-w-md w-full" data-testid="wp-gate-full">
              <p className="font-display text-xl font-bold">This party is full</p>
              <p className="text-sm text-foreground/60 mt-2">All {party.viewer_limit} seats are taken. Ask {party.host_name || "the host"} for a seat, or host your own Watch Party.</p>
              <a href={`/film/${party.film_id}`} className="mt-4 inline-block text-sm text-gold hover:underline" data-testid="wp-gate-own-party">Host your own Watch Party</a>
            </div>
          ) : (
            <AuthCard token={token} />
          )}
        </div>
      </div>
    </div>
  );
}
